import { motion, AnimatePresence } from "framer-motion";
import { Box } from "@chakra-ui/react";
import { setFunnelModalConfig } from "../FunnelModal";
import { FunnelOptionsTypes, FunnelContextTypes } from "../types";
import { FUNNEL_TYPES } from "../utils/contants";
import { useAuthFunnel } from "./useAuthFunnel";
import { useContentFunnel } from "./useContentFunnel";
import { FUNNEL_ANIMATION_DURATION_MS } from "../../slideInMenu/constants";
import { useMemo, useEffect } from "react";
import { useAccountData } from "../../../hooks/useAccountData";

const MotionBox = motion(Box);

// Determine if a funnel type is an auth funnel
const AUTH_FUNNEL_TYPES = [
  FUNNEL_TYPES.login,
  FUNNEL_TYPES.signup,
  FUNNEL_TYPES.signupEmailPassword,
];

function isAuthFunnelType(type?: FUNNEL_TYPES): boolean {
  return type ? AUTH_FUNNEL_TYPES.includes(type) : false;
}

export function useFunnel({
  type,
  context,
  options,
  onClose,
  setFunnelConfig = setFunnelModalConfig,
}: {
  type?: FUNNEL_TYPES;
  context?: FunnelContextTypes;
  options?: FunnelOptionsTypes;
  onClose?: () => void;
  setFunnelConfig?: typeof setFunnelModalConfig;
}) {
  const accountData = useAccountData();
  const showOfferPaywallModal = accountData?.offer_paywall ?? false;

  const shouldInterceptSubscriptionModalWithOfferPaywall =
    type === FUNNEL_TYPES.subscription &&
    showOfferPaywallModal &&
    !options?.skipOfferPaywallModal;

  const effectiveType = shouldInterceptSubscriptionModalWithOfferPaywall
    ? FUNNEL_TYPES.offerPaywall
    : type;

  // Intercept subscription and redirect to offerPaywall if feature flag is enabled
  useEffect(() => {
    if (shouldInterceptSubscriptionModalWithOfferPaywall) {
      setFunnelConfig({
        type: FUNNEL_TYPES.offerPaywall,
        context,
        options,
      });
    }
  }, [
    shouldInterceptSubscriptionModalWithOfferPaywall,
    setFunnelConfig,
    context,
    options,
  ]);

  const isAuthFunnel = isAuthFunnelType(effectiveType);

  // Only call the relevant hook based on funnel type
  // This eliminates double work on every render
  const authResult = useAuthFunnel(
    isAuthFunnel
      ? {
          setFunnelConfig,
          onCloseFunnel: onClose,
          openFunnelType: effectiveType,
          context,
          options,
        }
      : {
          setFunnelConfig,
          onCloseFunnel: undefined,
          openFunnelType: undefined,
          context: undefined,
          options: undefined,
        },
  );

  const contentResult = useContentFunnel(
    !isAuthFunnel
      ? {
          setFunnelConfig,
          onCloseFunnel: onClose,
          openFunnelType: effectiveType,
          context,
          options,
        }
      : {
          setFunnelConfig,
          onCloseFunnel: undefined,
          openFunnelType: undefined,
          context: undefined,
          options: undefined,
        },
  );

  // Wrap content with animation
  const wrapWithAnimation = (content: React.ReactNode, key: string) => {
    if (!content) return null;

    return (
      <AnimatePresence mode="wait">
        <MotionBox
          key={key}
          initial={{ opacity: 0, filter: "brightness(1.3)" }}
          animate={{ opacity: 1, filter: "brightness(1)" }}
          exit={{ opacity: 0, filter: "brightness(1.3)" }}
          transition={{ duration: FUNNEL_ANIMATION_DURATION_MS / 1000 }}
          width="100%"
        >
          {content}
        </MotionBox>
      </AnimatePresence>
    );
  };

  // Memoize the wrapped content based on the active funnel type
  const wrappedHeader = useMemo(() => {
    const header = isAuthFunnel
      ? authResult.funnelHeader
      : contentResult.funnelHeader;
    return wrapWithAnimation(header, `header-${effectiveType}`);
  }, [
    isAuthFunnel,
    authResult.funnelHeader,
    contentResult.funnelHeader,
    effectiveType,
  ]);

  const wrappedBody = useMemo(() => {
    const body = isAuthFunnel
      ? authResult.funnelBody
      : contentResult.funnelBody;
    return wrapWithAnimation(body, `body-${effectiveType}`);
  }, [
    isAuthFunnel,
    authResult.funnelBody,
    contentResult.funnelBody,
    effectiveType,
  ]);

  const wrappedFooter = useMemo(() => {
    const footer = isAuthFunnel
      ? authResult.funnelFooter
      : contentResult.funnelFooter;
    return wrapWithAnimation(footer, `footer-${effectiveType}`);
  }, [
    isAuthFunnel,
    authResult.funnelFooter,
    contentResult.funnelFooter,
    effectiveType,
  ]);

  const activeOnClose = isAuthFunnel
    ? authResult.onClose
    : contentResult.onClose;

  return {
    isAuthFunnel,
    onClose: activeOnClose,
    funnelHeader: wrappedHeader,
    funnelBody: wrappedBody,
    funnelFooter: wrappedFooter,
  };
}
